import React from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { useStatusPalette } from '../../../utils/statusColors';

export const monotonePalette = {
  surface: '#ffffff',
  surfaceSubtle: '#fafafa',
  border: '#e4e4e7',
  textPrimary: '#18181b',
  textSecondary: '#71717a',
  successBg: '#f0fdf4',
  successText: '#15803d',
  warningBg: '#fefce8',
  warningText: '#a16207',
  errorBg: '#fef2f2',
  errorText: '#b91c1c',
  // New tokens -- strictly monotone zinc scale
  focusRing: 'rgba(24,24,27,0.08)',
  focusRingError: 'rgba(185,28,28,0.06)',
  accent: '#18181b',
  accentHover: '#27272a',
  inputBorder: 'rgba(0,0,0,0.12)',
  inputBorderHover: 'rgba(0,0,0,0.24)',
  inputBorderFocus: '#18181b',
  inputBg: 'transparent',
  shadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
  fieldHoverBg: 'rgba(0,0,0,0.015)',
  chipBg: '#f4f4f5',
  chipText: '#52525b',
  chipBorder: '#d4d4d8',
  selectedBorder: '#18181b',
  selectedBg: 'rgba(0,0,0,0.03)',
  pageBg: '#fafafa',
};

const darkMonotonePalette = {
  surface: '#1a1a1e',
  surfaceSubtle: '#2c2c31',
  border: '#27272a',
  textPrimary: '#fafafa',
  textSecondary: '#a1a1aa',
  successBg: 'rgba(21,128,61,0.15)',
  successText: '#86efac',
  warningBg: 'rgba(161,98,7,0.15)',
  warningText: '#fbbf24',
  errorBg: 'rgba(185,28,28,0.15)',
  errorText: '#fca5a5',
  focusRing: 'rgba(250,250,250,0.08)',
  focusRingError: 'rgba(252,165,165,0.08)',
  accent: '#fafafa',
  accentHover: '#d4d4d8',
  inputBorder: 'rgba(255,255,255,0.12)',
  inputBorderHover: 'rgba(255,255,255,0.24)',
  inputBorderFocus: '#fafafa',
  inputBg: 'transparent',
  shadow: '0 1px 3px rgba(0,0,0,0.2), 0 8px 24px rgba(0,0,0,0.3)',
  fieldHoverBg: 'rgba(255,255,255,0.02)',
  chipBg: '#27272a',
  chipText: '#a1a1aa',
  chipBorder: '#3f3f46',
  selectedBorder: '#fafafa',
  selectedBg: 'rgba(255,255,255,0.04)',
  pageBg: '#1a1a1e',
};

export function useFormPalette() {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? darkMonotonePalette : monotonePalette;
}

const STATUS_LABELS = {
  NOT_STARTED: 'Not started',
  ACTIVE: 'Active',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
};

export function formatStatusLabel(value) {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (STATUS_LABELS[raw]) return STATUS_LABELS[raw];

  return raw
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatProgressTone(progress) {
  if (progress === 'DONE') return 'success';
  if (progress === 'CANCELLED') return 'error';
  if (progress === 'ACTIVE') return 'warning';
  return 'neutral';
}

export function FormSurface({ title, subtitle, rightSlot, children }) {
  const palette = useFormPalette();
  return (
    <Box
      sx={{
        border: `1px solid ${palette.border}`,
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: palette.surface,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${palette.border}`,
          backgroundColor: palette.surfaceSubtle,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          rowGap: 0.75,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: palette.textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: "11px", color: palette.textSecondary, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {rightSlot}
      </Box>
      <Box sx={{ p: 1.5, minWidth: 0, overflowX: "auto" }}>{children}</Box>
    </Box>
  );
}

export function StatusPill({ label, tone = 'neutral', formatLabel = true }) {
  const palette = useFormPalette();
  const statusPalette = useStatusPalette();
  const sxByTone = {
    success: {
      backgroundColor: palette.successBg,
      color: palette.successText,
      borderColor: palette === darkMonotonePalette ? 'rgba(21,128,61,0.3)' : '#bbf7d0',
    },
    warning: {
      backgroundColor: palette.warningBg,
      color: palette.warningText,
      borderColor: palette === darkMonotonePalette ? 'rgba(161,98,7,0.3)' : '#fde68a',
    },
    error: {
      backgroundColor: palette.errorBg,
      color: palette.errorText,
      borderColor: palette === darkMonotonePalette ? 'rgba(185,28,28,0.3)' : '#fecaca',
    },
    waiting: {
      backgroundColor: statusPalette.purple.bg,
      color: statusPalette.purple.color,
      borderColor: statusPalette.purple.border,
    },
    neutral: {
      backgroundColor: palette.surfaceSubtle,
      color: palette.textSecondary,
      borderColor: palette.border,
    },
  };

  const displayLabel = formatLabel ? formatStatusLabel(label) : label;

  return (
    <Chip
      size="small"
      label={displayLabel}
      sx={{
        height: '20px',
        borderRadius: '5px',
        border: '1px solid',
        fontSize: '10px',
        fontWeight: 600,
        ...sxByTone[tone],
      }}
    />
  );
}

/** Active forms/tasks show a purple "Waiting" pill aligned with session status colors. */
export function FormProgressChip({ progressStatus, isActive }) {
  if (isActive) {
    return <StatusPill label="Waiting" tone="waiting" formatLabel={false} />;
  }
  if (!progressStatus) return null;
  return <StatusPill label={progressStatus} tone={formatProgressTone(progressStatus)} />;
}
